import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useIsMobile } from 'hooks/useIsMobile';
import { ReactNode, useState } from 'react';
import { t } from 'utils/getTranslations';
import AddFilterPopover, { FilterPopoverSession } from './AddFilterPopover';
import FilterChip, { ResolveSelectedOptions } from './FilterChip';
import {
    cloneFilterState,
    filterChips,
    filterFieldGroups,
    FilterState,
    isShownByQuickFilter,
    withoutField,
    withoutHiddenValues,
} from './filterChips';

/** Reset and Clear all: the row's text links, one or the other by whether the list has views. */
const TEXT_LINK = 'text-link text-meta h-8 shrink-0 cursor-pointer px-1 whitespace-nowrap hover:underline';

type Props = {
    search: ReactNode;
    state: FilterState;
    onApply: (next: FilterState) => void;
    onResolveOptions: ResolveSelectedOptions;
    /** The list's own controls, at the row's right end. */
    actions: ReactNode;
    /** The quick-filter chips, between the search and the active filters' chips. They show every quick filter's
     *  state, so the row draws no chip for a quick filter, nor for a main filter whose quick filter shows its values. */
    quickFilters?: ReactNode;
    /** The selectors of main filters a quick-filter chip shows outside the quick-filter state, such as the
     *  period: the row draws no chip of their own. */
    shownByQuickFilters?: string[];
    /** Set while a list with saved views has moved off its open view: Reset, the last filter control before
     *  `actions`, takes the list back to the view. */
    onReset?: () => void;
    /** Set while a list without saved views has something to clear: Clear all, the last filter control before
     *  `actions`, empties every filter and the search. */
    onClearAll?: () => void;
    /** A tooltip after a field group's header, by the group's message key. */
    groupTooltips?: Record<string, string>;
};

/**
 * The new design's filter row: search, the quick-filter chips in place of the quick-filter checkboxes, a chip per
 * other active filter, + Filter in place of the "All filters" drawer, then Reset while a list with views has moved off
 * its view, or Clear all while a list without views has something to clear.
 */
export default function FilterBar({
    search,
    state,
    onApply,
    onResolveOptions,
    actions,
    quickFilters,
    shownByQuickFilters,
    onReset,
    onClearAll,
    groupTooltips,
}: Props) {
    const [session, setSession] = useState<FilterPopoverSession | null>(null);
    const dateFormat = useEffectiveDateFormat();
    const isMobile = useIsMobile();
    const chips = filterChips(state, (value) => format(new Date(value), dateFormat)).filter(
        (chip) =>
            !quickFilters ||
            (chip.field.kind === 'main' &&
                !isShownByQuickFilter(chip.field, state.quickFilterState) &&
                !shownByQuickFilters?.includes(chip.field.filter.selector))
    );
    const firstFieldKey = filterFieldGroups(state)[0]?.fields[0]?.key;
    const openOn = (fieldKey: string) => setSession({ fieldKey, draft: cloneFilterState(state) });

    return (
        <div className="flex flex-wrap items-center gap-2">
            {search}
            {quickFilters}
            {chips.map((chip) => (
                <FilterChip
                    key={chip.field.key}
                    chip={chip}
                    onOpen={() => openOn(chip.field.key)}
                    onRemove={() => onApply(withoutHiddenValues(withoutField(state, chip.field)))}
                    onResolveOptions={onResolveOptions}
                />
            ))}
            {firstFieldKey ? (
                <AddFilterPopover
                    // A window widened past `sm` with no field picked shows the first one, as it opens there.
                    session={
                        session && !isMobile && !session.fieldKey ? { ...session, fieldKey: firstFieldKey } : session
                    }
                    // Below `sm` the popover shows one pane at a time, and starts on the list of fields.
                    onOpen={() => openOn(isMobile ? '' : firstFieldKey)}
                    onClose={() => setSession(null)}
                    onChange={(update) => setSession((current) => (current ? update(current) : current))}
                    onApply={() => {
                        if (session) onApply(withoutHiddenValues(session.draft));
                        setSession(null);
                    }}
                    groupTooltips={groupTooltips}
                />
            ) : null}
            {onReset ? (
                <button type="button" onClick={onReset} className={TEXT_LINK}>
                    {t('views.reset')}
                </button>
            ) : null}
            {onClearAll ? (
                <button type="button" onClick={onClearAll} className={TEXT_LINK}>
                    {t('filters.clear-all')}
                </button>
            ) : null}
            <div className="ms-auto flex flex-wrap items-center gap-2">{actions}</div>
        </div>
    );
}
